import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PagePath } from '@monorepo/client-types';
import { SystemIcon } from '@monorepo/nova-icons';
import { CartStep } from '@monorepo/types';
import BalanceDialog from '../balance/balance-dialog';
import ExpiringCreditContent from './components/expiring-credit-content';
import ReceivedCreditContent from './components/received-credit-content';
import {
  CreditEventType,
  type CreditsEventsModalProps,
} from './credit-events.types';
import {
  StyledCreditModal,
  StyledCreditModalClose,
  StyledCreditModalSurface,
} from './styled';

export const CreditEventsModal = ({
  type,
  open,
  onClose,
  onViewCreditHistory,
  creditAmount = 100,
  onRedeem,
  cartAssets,
  onRedeemForAsset,
}: CreditsEventsModalProps) => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const [isBalanceDialogOpen, setIsBalanceDialogOpen] = useState(false);

  const goToPurchase = useCallback(() => {
    onClose();
    navigate(`${PagePath.Purchase}?step=${CartStep.Programs}`);
  }, [navigate, onClose]);

  const handleOpenBalanceDialog = useCallback(() => {
    onViewCreditHistory?.();
    setIsBalanceDialogOpen(true);
  }, [onViewCreditHistory]);

  const handleCloseBalanceDialog = useCallback(() => {
    setIsBalanceDialogOpen(false);
  }, []);

  const handleNewOrder = useCallback(() => {
    onClose();
    navigate(`${PagePath.Purchase}?step=${CartStep.Programs}`);
  }, [navigate, onClose]);

  const redeemHandler = onRedeem ?? goToPurchase;

  const content =
    type === CreditEventType.ReceivedCredit ? (
      <ReceivedCreditContent
        type={type}
        creditAmount={creditAmount}
        onRedeem={redeemHandler}
        onSeeAllCredits={handleOpenBalanceDialog}
        cartAssets={cartAssets}
        onRedeemForAsset={onRedeemForAsset}
        onNewOrder={handleNewOrder}
      />
    ) : (
      <ExpiringCreditContent
        type={type}
        creditAmount={creditAmount}
        onRedeem={redeemHandler}
        onSeeAllCredits={handleOpenBalanceDialog}
        cartAssets={cartAssets}
        onRedeemForAsset={onRedeemForAsset}
        onNewOrder={handleNewOrder}
      />
    );

  return (
    <>
      <StyledCreditModal open={open} onClose={onClose}>
        <StyledCreditModalSurface
          isExpiringCredit={type === CreditEventType.ExpiringCredit}
        >
          <StyledCreditModalClose
            onClick={onClose}
            aria-label={t('close')}
            size="small"
          >
            <SystemIcon iconName="X" size={24} color="primary" />
          </StyledCreditModalClose>

          {content}
        </StyledCreditModalSurface>
      </StyledCreditModal>

      <BalanceDialog
        open={isBalanceDialogOpen}
        onClose={handleCloseBalanceDialog}
      />
    </>
  );
};
